import React, { useEffect, useId, useState } from "react";
import { BWAT_DOMAINS, BWAT_REFERENCE_URL, HISTORY_OPTIONS, INTAKE_SECTIONS,
  buildCaseDraft, buildCaseText, bwatSummaryText, summarizeBwat } from "./caseIntake";
import "./CaseIntake.css";

export default function CaseIntake({ question, onInsert, onPendingChange, disabled = false, episodeActive = false, activeWoundId = "wound-1" }) {
  const prefix = useId();
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const [scoresOpen, setScoresOpen] = useState(false);
  const update = (id, value) => { setValues((old) => ({ ...old, [id]: value })); setError(null); };
  const preview = buildCaseText(values);
  useEffect(() => { onPendingChange(Boolean(preview)); }, [preview, onPendingChange]);
  const bwat = summarizeBwat(values);
  function insert() {
    try {
      const text = buildCaseDraft(question, values);
      onInsert(text);
      setValues({}); setError(null);
    } catch (problem) { setError(problem.message); }
  }
  return (
    <details className="case-intake">
      <summary>Structured case details (optional)</summary>
      <p>For one wound. Enter only information you have assessed or documented. Blank means not supplied, not absent.
        Include the side, units, date and result status where relevant. Do not enter patient identifiers.</p>
      {episodeActive && <p>Enter new observations for the current wound ({activeWoundId}). Earlier accepted findings remain in the episode.
        To correct a previous entry, begin the clinical question with “Correction:”. For a different wound, begin with “New wound:”.</p>}
      <p>This composes editable source text; it does not diagnose, stage a wound or select treatment.
        Insert it into the clinical question and review it before selecting Ask.</p>
      <fieldset disabled={disabled} className="case-intake-fields">
        <legend className="sr-only">Optional observations for this wound entry</legend>
        {INTAKE_SECTIONS.map((group) => (
          <details key={group.id} className="case-intake-group">
            <summary>{group.title}</summary>
            <div className="case-intake-grid">
              {group.fields.map((field) => (
                <div key={field.id} className="case-intake-field">
                  <label htmlFor={`${prefix}-${field.id}`}>{field.label}</label>
                  {field.kind === "history" ? (
                    <select id={`${prefix}-${field.id}`} value={values[field.id] || ""}
                      onChange={(event) => update(field.id, event.target.value)}>
                      {HISTORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  ) : (
                    <textarea id={`${prefix}-${field.id}`} value={values[field.id] || ""}
                      rows={2} maxLength={600} autoComplete="off"
                      onChange={(event) => update(field.id, event.target.value)} />
                  )}
                </div>
              ))}
            </div>
          </details>
        ))}
        <details className="case-intake-group" onToggle={(event) => setScoresOpen(event.currentTarget.open)}>
          <summary>BWAT: enter the thirteen item scores</summary>
          <p>Use the {scoresOpen ? <a href={BWAT_REFERENCE_URL} target="_blank" rel="noopener noreferrer">BWAT rating form</a> : "BWAT rating form"}.
            Enter your assessed 1–5 scores. Scores are not inferred from measurements or narrative.
            Odor, tunneling and specimen results remain separate observations, not extra score items.</p>
          <div className="case-intake-grid case-intake-scores">
            {BWAT_DOMAINS.map(({ id, label }) => (
              <div className="case-intake-field" key={id}>
                <label htmlFor={`${prefix}-bwat-${id}`}>BWAT {label} score</label>
                <select id={`${prefix}-bwat-${id}`} value={values[`bwat_${id}`] || ""}
                  onChange={(event) => update(`bwat_${id}`, event.target.value)}>
                  <option value="">Not assessed</option>
                  {[1, 2, 3, 4, 5].map((value) => <option key={value} value={String(value)}>{value}</option>)}
                </select>
              </div>
            ))}
          </div>
        </details>
        {bwat.completed > 0 && <p role="status">{bwatSummaryText(values)}</p>}
        {preview && <details className="case-intake-preview">
          <summary>Review the source text to be inserted</summary>
          <pre>{preview}</pre>
        </details>}
        {error && <p role="alert">{error}</p>}
        <div className="case-intake-actions">
          <button type="button" className="clear-btn" disabled={!preview} onClick={insert}>Insert details into question</button>
          <button type="button" className="clear-btn" onClick={() => { setValues({}); setError(null); }}>Reset these fields</button>
        </div>
        <p className="privacy-note">These fields stay in this page's memory. Inserting them does not send a request.
          The server's existing 4000-character question limit still applies; nothing is silently shortened.</p>
      </fieldset>
    </details>
  );
}
